// Mapping between SimpleNotation sampler sounds / instruments and General MIDI
// programs and MusicXML standard instrument-sound ids.

import { INSTRUMENTS, SOUNDS, instrumentDef, type InstrumentDef } from '../model/instruments';

interface SoundMap {
  /** 0-based General MIDI program. */
  program: number;
  /** MusicXML standard sound id (sounds.xml). */
  xml: string;
}

const SOUND_MAP: Record<string, SoundMap> = {
  gtr_nylon: { program: 24, xml: 'pluck.guitar.nylon-string' },
  gtr_steel: { program: 25, xml: 'pluck.guitar.steel-string' },
  gtr_jazz: { program: 26, xml: 'pluck.guitar.electric' },
  gtr_clean: { program: 27, xml: 'pluck.guitar.electric' },
  gtr_dist: { program: 30, xml: 'pluck.guitar.electric' },
  bass_acoustic: { program: 32, xml: 'pluck.bass.acoustic' },
  bass_finger: { program: 33, xml: 'pluck.bass.electric' },
  bass_pick: { program: 34, xml: 'pluck.bass.electric' },
  banjo: { program: 105, xml: 'pluck.banjo' },
  mandolin: { program: 25, xml: 'pluck.mandolin' },
  ukulele: { program: 24, xml: 'pluck.ukulele' },
  harp: { program: 46, xml: 'pluck.harp' },
  harpsichord: { program: 6, xml: 'keyboard.harpsichord' },
  piano: { program: 0, xml: 'keyboard.piano' },
  epiano: { program: 4, xml: 'keyboard.piano.electric' },
  organ: { program: 19, xml: 'keyboard.organ' },
  accordion: { program: 21, xml: 'keyboard.accordion' },
  harmonica: { program: 22, xml: 'wind.reed.harmonica' },
  bagpipe: { program: 109, xml: 'wind.pipes.bagpipes' },
  violin: { program: 40, xml: 'strings.violin' },
  viola: { program: 41, xml: 'strings.viola' },
  cello: { program: 42, xml: 'strings.cello' },
  contrabass: { program: 43, xml: 'strings.contrabass' },
  flute: { program: 73, xml: 'wind.flutes.flute' },
  recorder: { program: 74, xml: 'wind.flutes.recorder' },
  clarinet: { program: 71, xml: 'wind.reed.clarinet' },
  oboe: { program: 68, xml: 'wind.reed.oboe' },
  bassoon: { program: 70, xml: 'wind.reed.bassoon' },
  sax_alto: { program: 65, xml: 'wind.reed.saxophone.alto' },
  sax_tenor: { program: 66, xml: 'wind.reed.saxophone.tenor' },
  sax_bari: { program: 67, xml: 'wind.reed.saxophone.baritone' },
  trumpet: { program: 56, xml: 'brass.trumpet' },
  trombone: { program: 57, xml: 'brass.trombone' },
  horn: { program: 60, xml: 'brass.french-horn' },
  tuba: { program: 58, xml: 'brass.tuba' },
  marimba: { program: 12, xml: 'pitched-percussion.marimba' },
  vibraphone: { program: 11, xml: 'pitched-percussion.vibraphone' },
  glockenspiel: { program: 9, xml: 'pitched-percussion.glockenspiel' },
  steel_drum: { program: 114, xml: 'metal.steel-drums' },
  timpani: { program: 47, xml: 'drum.timpani' },
  synth_lead: { program: 80, xml: 'synth.tone.square' },
  choir: { program: 52, xml: 'voice.aa' },
  drums: { program: 0, xml: 'drum.group.set' },
};

/** 0-based GM program for a sampler sound. */
export function gmProgramOf(sound: string): number {
  return SOUND_MAP[sound]?.program ?? 0;
}

export function xmlSoundOf(sound: string): string | undefined {
  return SOUND_MAP[sound]?.xml;
}

/** Sampler sound for a 0-based GM program. */
export function soundForProgram(program: number): string {
  const exact = Object.entries(SOUND_MAP).find(([id, m]) => m.program === program && id !== 'drums' && id !== 'mandolin' && id !== 'ukulele');
  if (exact) return exact[0];
  return instrumentForProgram(program).sound;
}

/** Sampler sound from a MusicXML instrument-sound id, disambiguated by GM program. */
export function soundForXml(xmlSound: string | undefined, program?: number): string | undefined {
  if (!xmlSound) return undefined;
  const s = xmlSound.trim().toLowerCase();
  let cands = Object.entries(SOUND_MAP).filter(([, m]) => m.xml === s);
  if (!cands.length) cands = Object.entries(SOUND_MAP).filter(([, m]) => s.startsWith(m.xml) || m.xml.startsWith(s));
  if (!cands.length) {
    // Family fallbacks.
    if (s.startsWith('pluck.guitar')) return program !== undefined && program >= 24 && program <= 31 ? soundForProgram(program) : 'gtr_steel';
    if (s.startsWith('pluck.bass')) return 'bass_finger';
    if (s.startsWith('keyboard.piano')) return 'piano';
    if (s.startsWith('keyboard.organ')) return 'organ';
    if (s.startsWith('drum') || s.startsWith('metal') || s.startsWith('wood')) return 'drums';
    if (s.startsWith('voice')) return 'choir';
    if (s.startsWith('synth')) return 'synth_lead';
    return undefined;
  }
  if (program !== undefined) {
    const p = cands.find(([, m]) => m.program === program);
    if (p) return p[0];
  }
  return cands[0][0];
}

export function isKnownSound(id: string): boolean {
  return SOUNDS.some((s) => s.id === id);
}

const byId = (id: string): InstrumentDef => instrumentDef(id);

/** Closest instrument definition for a 0-based GM program (channel 10 = drums handled by the caller). */
export function instrumentForProgram(program: number, minPitch?: number): InstrumentDef {
  const p = Math.max(0, Math.min(127, Math.round(program)));
  if (p <= 3) return byId('piano');
  if (p <= 5) return byId('electric-piano');
  if (p === 6 || p === 7) return byId('harpsichord');
  if (p === 8 || p === 9 || p === 10 || p === 14) return byId('glockenspiel');
  if (p === 11) return byId('vibraphone');
  if (p >= 12 && p <= 13) return byId('marimba');
  if (p === 15) return byId('piano');
  if (p >= 16 && p <= 20) return byId('organ');
  if (p === 21 || p === 23) return byId('accordion');
  if (p === 22) return byId('harmonica');
  if (p === 24) return byId('classical-guitar');
  if (p === 25) return byId('acoustic-guitar');
  if (p >= 26 && p <= 28) return byId('electric-guitar');
  if (p >= 29 && p <= 31) return byId('electric-guitar-dist');
  if (p === 32) return byId('acoustic-bass');
  if (p >= 33 && p <= 39) return minPitch !== undefined && minPitch < 28 ? byId('bass-guitar-5') : byId('bass-guitar');
  if (p === 40) return byId('violin');
  if (p === 41) return byId('viola');
  if (p === 42) return byId('cello');
  if (p === 43) return byId('contrabass');
  if (p >= 44 && p <= 45) return byId('violin');
  if (p === 46) return byId('harp');
  if (p === 47) return byId('timpani');
  if (p >= 48 && p <= 51) return byId('violin');
  if (p >= 52 && p <= 54) return byId('soprano-voice');
  if (p === 55) return byId('synth');
  if (p === 56 || p === 59) return byId('trumpet');
  if (p === 57) return byId('trombone');
  if (p === 58) return byId('tuba');
  if (p === 60) return byId('french-horn');
  if (p >= 61 && p <= 63) return byId('trumpet');
  if (p === 64) return byId('soprano-sax');
  if (p === 65) return byId('alto-sax');
  if (p === 66) return byId('tenor-sax');
  if (p === 67) return byId('baritone-sax');
  if (p === 68) return byId('oboe');
  if (p === 69) return byId('english-horn');
  if (p === 70) return byId('bassoon');
  if (p === 71) return byId('clarinet');
  if (p === 72) return byId('piccolo');
  if (p === 73) return byId('flute');
  if (p === 74) return byId('recorder');
  if (p === 78) return byId('tin-whistle');
  if (p >= 75 && p <= 79) return byId('flute');
  if (p >= 80 && p <= 103) return byId('synth');
  if (p === 104) return byId('acoustic-guitar');
  if (p === 105) return byId('banjo');
  if (p >= 106 && p <= 107) return byId('acoustic-guitar');
  if (p === 108) return byId('marimba');
  if (p === 109) return byId('bagpipes');
  if (p === 110) return byId('fiddle');
  if (p === 111) return byId('oboe');
  if (p === 114) return byId('steel-drums');
  if (p >= 112 && p <= 119) return byId('drums');
  return byId('piano');
}

/** Instrument whose display name matches exactly (case-insensitive). */
export function instrumentByName(name: string | undefined): InstrumentDef | undefined {
  if (!name) return undefined;
  const n = name.trim().toLowerCase();
  return INSTRUMENTS.find((i) => i.name.toLowerCase() === n || i.id === n);
}

/** Best-effort instrument from free text (a part name). */
export function instrumentByKeywords(text: string | undefined): InstrumentDef | undefined {
  if (!text) return undefined;
  const t = text.toLowerCase();
  const rules: Array<[RegExp, string]> = [
    [/steel drum/, 'steel-drums'],
    [/bass clarinet/, 'bass-clarinet'],
    [/\bbass voice|basso/, 'bass-voice'],
    [/\b(drum|kit|percussion|batterie|schlagzeug)/, 'drums'],
    [/\b(5|five)[- ]?string bass/, 'bass-guitar-5'],
    [/acoustic bass|upright bass|double bass|contrabass|kontrabass/, 'contrabass'],
    [/bass guitar|electric bass|\bbass\b|e-bass|basse|bajo/, 'bass-guitar'],
    [/12[- ]string/, 'guitar-12'],
    [/7[- ]string/, 'guitar-7'],
    [/baritone guitar/, 'baritone-guitar'],
    [/classical|nylon|spanish|concert guitar/, 'classical-guitar'],
    [/dist|overdrive|lead guitar|rhythm guitar/, 'electric-guitar-dist'],
    [/electric guitar|e-gitarre|clean guitar|jazz guitar/, 'electric-guitar'],
    [/guitar|gitarre|guitare|guitarra|chitarra|gtr/, 'acoustic-guitar'],
    [/ukulele|\buke\b/, 'ukulele'],
    [/mandolin/, 'mandolin'],
    [/banjo/, 'banjo'],
    [/bouzouki/, 'bouzouki'],
    [/lute|laute/, 'lute'],
    [/dobro|resonator/, 'dobro'],
    [/pedal steel/, 'pedal-steel'],
    [/lap steel/, 'lap-steel'],
    [/electric piano|e-piano|rhodes|wurli/, 'electric-piano'],
    [/piano|klavier|keyboard/, 'piano'],
    [/harpsichord|cembalo/, 'harpsichord'],
    [/organ|orgel/, 'organ'],
    [/accordion|akkordeon/, 'accordion'],
    [/harmonica/, 'harmonica'],
    [/viola|bratsche/, 'viola'],
    [/violoncello|cello/, 'cello'],
    [/fiddle/, 'fiddle'],
    [/violin|geige|violon/, 'violin'],
    [/harp|harfe/, 'harp'],
    [/piccolo/, 'piccolo'],
    [/alto flute/, 'alto-flute'],
    [/flute|flöte|flauto/, 'flute'],
    [/recorder|blockflöte/, 'recorder'],
    [/whistle/, 'tin-whistle'],
    [/bass clarinet/, 'bass-clarinet'],
    [/clarinet|klarinette/, 'clarinet'],
    [/english horn|cor anglais/, 'english-horn'],
    [/oboe/, 'oboe'],
    [/bassoon|fagott/, 'bassoon'],
    [/soprano sax/, 'soprano-sax'],
    [/tenor sax/, 'tenor-sax'],
    [/baritone sax|bari sax/, 'baritone-sax'],
    [/sax/, 'alto-sax'],
    [/flugelhorn/, 'flugelhorn'],
    [/cornet/, 'cornet'],
    [/trumpet|trompete/, 'trumpet'],
    [/trombone|posaune/, 'trombone'],
    [/euphonium/, 'euphonium'],
    [/tuba/, 'tuba'],
    [/horn/, 'french-horn'],
    [/glockenspiel/, 'glockenspiel'],
    [/marimba|xylophone/, 'marimba'],
    [/vibraphone|vibes/, 'vibraphone'],
    [/timpani|pauken/, 'timpani'],
    [/steel drum/, 'steel-drums'],
    [/synth/, 'synth'],
    [/bagpipe/, 'bagpipes'],
    [/soprano/, 'soprano-voice'],
    [/mezzo|alto\b|contralto/, 'mezzo-voice'],
    [/tenor/, 'tenor-voice'],
    [/baritone/, 'baritone-voice'],
    [/\bbass voice|basso/, 'bass-voice'],
    [/voice|vocal|voix|stimme|choir|chor|sing/, 'soprano-voice'],
  ];
  for (const [re, id] of rules) if (re.test(t)) return byId(id);
  return undefined;
}

/** A fretted instrument for a tuning (sounding MIDI high → low). */
export function fretInstrumentForTuning(tuning: readonly number[]): InstrumentDef {
  const low = Math.min(...tuning);
  if (tuning.length <= 5 && low < 36) return tuning.length === 5 ? byId('bass-guitar-5') : byId('bass-guitar');
  if (tuning.length === 7) return byId('guitar-7');
  if (tuning.length === 4 && low >= 55) return byId(tuning[0] >= 76 ? 'mandolin' : 'ukulele');
  if (tuning.length === 5 && low >= 50) return byId('banjo');
  return byId('acoustic-guitar');
}
